// Geometria de pantalla del mapa de la torre, independiente del SDK que lo
// dibuje (ADR-020, G6). Logica pura en pixeles del contenedor del mapa: el
// renderer proyecta lat/lng a pixeles y le pide aca que hacer. Asi el
// comportamiento de MapLibre (tamano por zoom, encuadre del seleccionado,
// tarjeta anclada) se porta a Google sin reescribirlo ni perder sus tests.
/** Y de Web Mercator normalizada (0 arriba, 1 abajo). */
function mercatorY(lat) {
    const s = Math.sin((lat * Math.PI) / 180);
    return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
}
/**
 * Pixel del contenedor de un punto, a partir de los limites visibles del mapa.
 * Vale para un mapa sin inclinacion ni rotacion, que es como la torre crea el
 * de Google (tilt y heading bloqueados): alcanza con los limites y el tamano,
 * sin depender de la proyeccion interna del SDK.
 */
export function projectToContainer(point, box, size) {
    // Un recuadro que cruza el antimeridiano trae east < west.
    const wraps = box.east < box.west;
    const east = wraps ? box.east + 360 : box.east;
    const lng = wraps && point.lng < box.west ? point.lng + 360 : point.lng;
    const x = ((lng - box.west) / (east - box.west)) * size.width;
    const top = mercatorY(box.north);
    const y = ((mercatorY(point.lat) - top) / (mercatorY(box.south) - top)) * size.height;
    return { x, y };
}
/**
 * Interpolacion por zoom con la misma curva que usaba MapLibre
 * (`["interpolate", ["exponential", base], ["zoom"], ...]`): con base 1 es
 * lineal; con base > 1 crece mas rapido cerca del zoom mayor. Fuera del rango
 * se queda en el extremo.
 */
export function interpolateByZoom(zoom, stops, base = 1) {
    const first = stops[0];
    const last = stops.at(-1);
    if (first === undefined || last === undefined)
        return 0;
    if (zoom <= first[0])
        return first[1];
    if (zoom >= last[0])
        return last[1];
    for (let i = 1; i < stops.length; i++) {
        const [z1, v1] = stops[i];
        const [z0, v0] = stops[i - 1];
        if (zoom > z1)
            continue;
        const span = z1 - z0;
        const progress = zoom - z0;
        const t = base === 1 ? progress / span : (base ** progress - 1) / (base ** span - 1);
        return v0 + (v1 - v0) * t;
    }
    return last[1];
}
// Mismas paradas que las capas de MapLibre (capa-vehiculos, capa-vehiculo-
// seleccionado, capa-seleccion-halo, capa-paradas): el operador ve los
// vehiculos del mismo tamano con los dos mapas.
const VEHICLE_SCALE = [
    [11, 0.35],
    [14, 0.7],
    [16, 1],
];
const SELECTED_VEHICLE_SCALE = [
    [11, 0.5],
    [14, 0.95],
    [16, 1.3],
];
const HALO_RADIUS_PX = [
    [11, 12],
    [16, 26],
];
const STOP_RADIUS_PX = [
    [11, 3],
    [15, 5.5],
];
const SELECTED_STOP_RADIUS_PX = [
    [11, 6],
    [15, 9],
];
export function markerSizing(zoom) {
    return {
        vehicleScale: interpolateByZoom(zoom, VEHICLE_SCALE, 1.5),
        selectedVehicleScale: interpolateByZoom(zoom, SELECTED_VEHICLE_SCALE, 1.5),
        haloRadiusPx: interpolateByZoom(zoom, HALO_RADIUS_PX),
        stopRadiusPx: interpolateByZoom(zoom, STOP_RADIUS_PX),
        selectedStopRadiusPx: interpolateByZoom(zoom, SELECTED_STOP_RADIUS_PX),
    };
}
// ---------- encuadre del vehiculo seleccionado ----------
/** Margen comodo contra los bordes del mapa, en px. */
const COMFORT_MARGIN_PX = 60;
/**
 * Franja que tapa la ficha del viaje, que se abre a la derecha sobre el mapa:
 * 400 px, o el 45 % del ancho en pantallas angostas.
 */
export function detailReservePx(mapWidth) {
    return Math.min(400, mapWidth * 0.45);
}
/**
 * Cuanto desplazar el mapa (px, para `panBy`) para traer el punto al centro
 * de la zona libre. `null` si ya esta en la zona comoda: seleccionar un
 * vehiculo visible no mueve el mapa.
 */
export function panToComfort(point, map) {
    const reserve = detailReservePx(map.width);
    const visible = point.x > COMFORT_MARGIN_PX &&
        point.x < map.width - reserve &&
        point.y > COMFORT_MARGIN_PX &&
        point.y < map.height - COMFORT_MARGIN_PX;
    if (visible)
        return null;
    const freeCenter = { x: (map.width - reserve) / 2, y: map.height / 2 };
    return { x: point.x - freeCenter.x, y: point.y - freeCenter.y };
}
// ---------- tarjeta de parada anclada al punto ----------
const CARD_GAP_PX = 16;
const CARD_EDGE_PX = 12;
/**
 * Franja inferior reservada a la atribucion de Google (logo a la izquierda,
 * terminos a la derecha). ADR-020 R6: ningun panel propio la tapa.
 */
export const ATTRIBUTION_RESERVE_PX = 28;
/**
 * Esquina superior izquierda de la tarjeta: a la derecha del punto; si no
 * entra, a la izquierda; siempre adentro del mapa y sobre la atribucion.
 */
export function placeStopCard(point, card, map) {
    const maxX = map.width - card.width - CARD_EDGE_PX;
    const maxY = map.height - card.height - ATTRIBUTION_RESERVE_PX;
    let x = point.x + CARD_GAP_PX;
    if (x + card.width > map.width - CARD_EDGE_PX)
        x = point.x - CARD_GAP_PX - card.width;
    x = Math.max(CARD_EDGE_PX, Math.min(x, maxX));
    const y = Math.max(CARD_EDGE_PX, Math.min(point.y - card.height / 2, maxY));
    return { x: Math.round(x), y: Math.round(y) };
}
/** Margen del area de operacion contra los bordes (el padding de antes). */
const AREA_MARGINS = { top: 24, right: 24, bottom: 24 + ATTRIBUTION_RESERVE_PX, left: 24 };
/**
 * Margen del punto de la planta contra los bordes. La etiqueta "PLANTA" mide
 * unos 70 x 20 px; en Google se dibuja encima del punto (ancla abajo al
 * centro) y en MapLibre centrada en el. Abajo ademas esta la atribucion
 * (ADR-020 R6), que ningun marcador propio tapa.
 */
export const SITE_MARGINS = { top: 56, right: 56, bottom: 40 + ATTRIBUTION_RESERVE_PX, left: 56 };
/** Tope de zoom del encuadre inicial: con un area chica no se acerca de mas. */
const FRAME_MAX_ZOOM = 15;
function mercatorX(lng) {
    return (lng + 180) / 360;
}
function latFromMercatorY(y) {
    const n = Math.PI * (1 - 2 * y);
    return (Math.atan(Math.sinh(n)) * 180) / Math.PI;
}
/**
 * Mayor escala (px por unidad de mundo) en un eje que deja cada punto con su
 * margen: para todo par i, j con i despues de j, la distancia entre ellos
 * tiene que entrar en el lado menos el margen final de i y el inicial de j.
 */
function maxScale(coords, side) {
    let scale = Number.POSITIVE_INFINITY;
    for (const i of coords) {
        if (side - i.start - i.end <= 0)
            return 0;
        for (const j of coords) {
            const span = i.v - j.v;
            if (span <= 0)
                continue;
            scale = Math.min(scale, (side - i.end - j.start) / span);
        }
    }
    return scale;
}
/**
 * Centro en un eje para una escala dada: el medio del intervalo de centros
 * que deja todos los puntos con su margen. Con una escala no mayor que la de
 * `maxScale` el intervalo nunca esta vacio.
 */
function centerOnAxis(coords, side, scale) {
    let low = Number.NEGATIVE_INFINITY;
    let high = Number.POSITIVE_INFINITY;
    for (const c of coords) {
        low = Math.max(low, c.v - (side / 2 - c.end) / scale);
        high = Math.min(high, c.v + (side / 2 - c.start) / scale);
    }
    return (low + high) / 2;
}
/**
 * Encuadre inicial de la torre (AT-18): el area de operacion entera y el pin
 * de la planta con margen en los cuatro lados, aunque la planta este sobre el
 * borde del area o fuera de ella. Lo calcula la torre y no el `fitBounds` del
 * SDK: asi Google (baldosa de 256 px) y MapLibre (512 px) encuadran igual, y
 * la planta no depende del padding ni del redondeo de zoom de cada uno.
 *
 * `null` si el contenedor todavia no tiene tamano: el renderer espera al
 * primer tamano real.
 */
export function initialCamera(area, site, size, tileSize) {
    if (size.width <= 0 || size.height <= 0)
        return null;
    const west = Math.min(area.lngMin, area.lngMax);
    const east = Math.max(area.lngMin, area.lngMax);
    const south = Math.min(area.latMin, area.latMax);
    const north = Math.max(area.latMin, area.latMax);
    const anchors = [
        { x: mercatorX(west), y: mercatorY(north), margins: AREA_MARGINS },
        { x: mercatorX(east), y: mercatorY(south), margins: AREA_MARGINS },
        { x: mercatorX(site.lng), y: mercatorY(site.lat), margins: SITE_MARGINS },
    ];
    const xs = anchors.map((a) => ({ v: a.x, start: a.margins.left, end: a.margins.right }));
    const ys = anchors.map((a) => ({ v: a.y, start: a.margins.top, end: a.margins.bottom }));
    const maxWorld = tileSize * 2 ** FRAME_MAX_ZOOM;
    const scale = Math.min(maxScale(xs, size.width), maxScale(ys, size.height), maxWorld);
    if (!(scale > 0)) {
        // Contenedor mas chico que los margenes: centrado en la planta, zoom de ciudad.
        return { center: { lat: site.lat, lng: site.lng }, zoom: 11 };
    }
    const cx = centerOnAxis(xs, size.width, scale);
    const cy = centerOnAxis(ys, size.height, scale);
    return {
        center: { lat: latFromMercatorY(cy), lng: cx * 360 - 180 },
        zoom: Math.log2(scale / tileSize),
    };
}
/**
 * Pixel del contenedor de un punto para una camara dada (la inversa del
 * encuadre). La usan los tests de geometria y sirve para verificar que la
 * planta quedo adentro sin depender del SDK.
 */
export function projectWithCamera(point, camera, size, tileSize) {
    const world = tileSize * 2 ** camera.zoom;
    return {
        x: size.width / 2 + (mercatorX(point.lng) - mercatorX(camera.center.lng)) * world,
        y: size.height / 2 + (mercatorY(point.lat) - mercatorY(camera.center.lat)) * world,
    };
}
//# sourceMappingURL=mapViewport.js.map